import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    Card,
    CardContent,
    Button,
    Typography,
    Box,
    Grid,
    TextField,
    InputAdornment,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CardMedia,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import {
    DirectionsCar as CarIcon,
    Schedule as ScheduleIcon,
    LocalGasStation as FuelIcon,
    Speed as OdometerIcon,
    Payment as PaymentIcon,
    CheckCircle as CheckIcon,
    PhotoCamera as PhotoCameraIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { Booking } from '../types/booking';
import type { ExpenseMutationData } from '../types/expense';
import PaymentDialog from './PaymentDialog';
import { bookingService } from '../services/bookingService';
import expenseService from '../services/expenseService';

interface HandoverCompleteSummary {
    trackedExpenseCount: number;
    failedExpenseCount: number;
    photoCount?: number;
}

interface HandoverWindowProps {
    open: boolean;
    booking: Booking | null;
    onClose: () => void;
    onHandoverComplete: (
        updatedBooking: Booking,
        summary?: HandoverCompleteSummary
    ) => void;
}

const FUEL_LEVELS = [
    { value: 0, key: 'empty', defaultLabel: 'Empty (0%)' },
    { value: 25, key: 'quarter', defaultLabel: 'Quarter (25%)' },
    { value: 50, key: 'half', defaultLabel: 'Half (50%)' },
    { value: 75, key: 'threeQuarter', defaultLabel: 'Three-quarter (75%)' },
    { value: 100, key: 'full', defaultLabel: 'Full (100%)' }
];

const toLocalDateTimeInputValue = (date: Date) => {
    const offsetMs = date.getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

// Start-of-day boundary, mirroring the rule POSVehicleHandover uses to bucket
// bookings. Comparing by calendar day (not exact timestamp) keeps this dialog's
// default in sync with the list: a booking the list shows as "due today" must
// default to "Now", never silently to "Schedule later".
const startOfDay = (date: Date) => {
    const boundary = new Date(date);
    boundary.setHours(0, 0, 0, 0);
    return boundary;
};

const HandoverWindow: React.FC<HandoverWindowProps> = ({
    open,
    booking,
    onClose,
    onHandoverComplete
}) => {
    const { t } = useTranslation();
    const [handoverType, setHandoverType] = useState<'now' | 'later'>('now');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Handover Form State
    const [handoverDateTime, setHandoverDateTime] = useState('');
    const [scheduledHandoverDateTime, setScheduledHandoverDateTime] = useState('');
    const [contractReference, setContractReference] = useState('');
    const [fuelLevel, setFuelLevel] = useState<number | ''>('');
    const [odometer, setOdometer] = useState<number | ''>('');
    const [notes, setNotes] = useState('');
    const [fuelExpenseAmount, setFuelExpenseAmount] = useState<number | ''>('');
    const [fuelExpenseDetails, setFuelExpenseDetails] = useState('');
    const [otherExpenseAmount, setOtherExpenseAmount] = useState<number | ''>('');
    const [otherExpenseDetails, setOtherExpenseDetails] = useState('');
    const [handoverPhotos, setHandoverPhotos] = useState<string[]>([]);

    // Payment State
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const photoInputRef = useRef<HTMLInputElement | null>(null);

    // Track current booking state locally to reflect real-time payment updates
    const [currentBooking, setCurrentBooking] = useState<Booking | null>(booking);

    const compressImageFile = (file: File) =>
        new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(new Error(t('pos.handover.photoReadFailed')));
            reader.onload = () => {
                if (typeof reader.result !== 'string') {
                    reject(new Error(t('pos.handover.photoReadFailed')));
                    return;
                }
                const img = new Image();
                img.onerror = () => reject(new Error(t('pos.handover.photoReadFailed')));
                img.onload = () => {
                    const maxDimension = 1280;
                    const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
                    const targetWidth = Math.round(img.width * scale);
                    const targetHeight = Math.round(img.height * scale);

                    const canvas = document.createElement('canvas');
                    canvas.width = targetWidth;
                    canvas.height = targetHeight;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error(t('pos.handover.photoReadFailed')));
                        return;
                    }
                    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
                    // JPEG @ 0.72 keeps each photo well under ~300KB so the
                    // booking handover payload fits within reverse-proxy body limits.
                    resolve(canvas.toDataURL('image/jpeg', 0.72));
                };
                img.src = reader.result;
            };
            reader.readAsDataURL(file);
        });

    const handlePhotoFiles = async (files: FileList | null) => {
        if (!files || files.length === 0) return;

        const maxPhotos = 4;
        const maxFileSize = 10 * 1024 * 1024;
        const selectedFiles = Array.from(files);

        const validFiles: File[] = [];
        for (const file of selectedFiles) {
            if (!file.type.startsWith('image/')) {
                setError(t('pos.handover.photoTypeError'));
                continue;
            }

            if (file.size > maxFileSize) {
                setError(t('pos.handover.photoSizeError'));
                continue;
            }

            validFiles.push(file);
        }

        if (!validFiles.length) return;

        try {
            const remainingSlots = Math.max(0, maxPhotos - handoverPhotos.length);
            const filesToAdd = validFiles.slice(0, remainingSlots);

            if (validFiles.length > filesToAdd.length) {
                setError(t('pos.handover.photoLimitError', { count: maxPhotos }));
            } else {
                setError(null);
            }

            const dataUrls = await Promise.all(filesToAdd.map((file) => compressImageFile(file)));
            setHandoverPhotos((prev) => [...prev, ...dataUrls]);
        } catch (err) {
            console.error('Failed to add handover photos:', err);
            setError(t('pos.handover.photoUploadFailed'));
        } finally {
            if (photoInputRef.current) {
                photoInputRef.current.value = '';
            }
        }
    };

    const removeHandoverPhoto = (index: number) => {
        setHandoverPhotos((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
    };

    // Sync currentBooking with booking prop changes
    useEffect(() => {
        if (booking) {
            setCurrentBooking(booking);
            // Default odometer from vehicle if available
            setOdometer(booking.vehicleId && typeof booking.vehicleId === 'object' ? (booking.vehicleId as any).mileage || '' : '');
            setContractReference((booking.contractReference || '').trim());
            setNotes('');
            setFuelExpenseAmount('');
            setFuelExpenseDetails('');
            setOtherExpenseAmount('');
            setOtherExpenseDetails('');
            setHandoverPhotos([]);
        }
    }, [booking]);

    // Initialize date with current time
    useEffect(() => {
        if (open) {
            const now = new Date();
            const formatted = toLocalDateTimeInputValue(now);
            setHandoverDateTime(formatted);
            setContractReference((booking?.contractReference || '').trim());
            setNotes('');
            setFuelExpenseAmount('');
            setFuelExpenseDetails('');
            setOtherExpenseAmount('');
            setOtherExpenseDetails('');
            setHandoverPhotos([]);

            // Default the mode using the SAME calendar-day rule the handover list
            // uses to decide a booking is "due today / overdue". If the effective
            // handover day is today or earlier, default to "Now" — otherwise the
            // dialog would silently pre-select "Schedule later", keep the booking
            // `confirmed`, and make it reappear in the ready list (the reschedule
            // loop users were hitting). Use scheduledHandoverTime when present so a
            // previously-scheduled booking is evaluated against its real target,
            // not just startDate.
            const effectiveHandover = booking?.handoverChecklist?.scheduledHandoverTime || booking?.startDate;
            const effectiveDate = effectiveHandover ? new Date(effectiveHandover) : null;
            const isFutureDay = !!effectiveDate
                && !Number.isNaN(effectiveDate.getTime())
                && startOfDay(effectiveDate).getTime() > startOfDay(now).getTime();

            if (isFutureDay && effectiveDate) {
                setHandoverType('later');
                setScheduledHandoverDateTime(toLocalDateTimeInputValue(effectiveDate));
            } else {
                setHandoverType('now');
                setScheduledHandoverDateTime(formatted);
            }
        }
    }, [open, booking]);

    if (!currentBooking) return null;

    const balanceDue = currentBooking.pricing.totalAmount - currentBooking.pricing.paidAmount;

    const buildHandoverExpensePayloads = (expenseDate: string): ExpenseMutationData[] => {
        const vehicleId =
            typeof currentBooking.vehicleId === 'object'
                ? currentBooking.vehicleId._id
                : currentBooking.vehicleId;

        const vehicleLabel =
            typeof currentBooking.vehicleId === 'object'
                ? `${currentBooking.vehicleId.make} ${currentBooking.vehicleId.model}`.trim()
                : currentBooking.bookingNumber;

        const basePayload = {
            origin: 'vehicle_event' as const,
            vehicleId: vehicleId || undefined,
            bookingId: currentBooking._id,
            expenseDate,
            paymentMethod: 'cash' as const,
            paymentStatus: 'paid' as const,
            status: 'approved' as const,
            sourceLabel: `Handover - ${currentBooking.bookingNumber}`,
            notes: 'Recorded during vehicle handover',
        };

        const expenses: ExpenseMutationData[] = [];

        if (Number(fuelExpenseAmount) > 0) {
            expenses.push({
                ...basePayload,
                category: 'vehicle_fuel',
                amount: Number(fuelExpenseAmount),
                description: fuelExpenseDetails.trim() || `Fuel expense for ${vehicleLabel}`,
                notes: fuelExpenseDetails.trim() || basePayload.notes,
            });
        }

        if (Number(otherExpenseAmount) > 0) {
            expenses.push({
                ...basePayload,
                category: 'vehicle_washing',
                amount: Number(otherExpenseAmount),
                description: otherExpenseDetails.trim() || `Washing expense for ${vehicleLabel}`,
                notes: otherExpenseDetails.trim() || basePayload.notes,
            });
        }

        return expenses;
    };

    const handlePaymentSuccess = (updatedBooking: Booking) => {
        setCurrentBooking(updatedBooking);
        setPaymentDialogOpen(false);
    };

    const handleAddPayment = async (paymentData: any) => {
        const result = await bookingService.addPayment(currentBooking._id, paymentData);
        handlePaymentSuccess(result.booking);
    };

    // Defensive cap: even after compression, refuse to send a payload
    // larger than ~3MB total so we stay well below any reverse-proxy limit.
    const MAX_PHOTOS_PAYLOAD_BYTES = 3 * 1024 * 1024;
    const trimPhotosForPayload = (photos: string[]): string[] => {
        const kept: string[] = [];
        let total = 0;
        for (const photo of photos) {
            total += photo.length;
            if (total > MAX_PHOTOS_PAYLOAD_BYTES) break;
            kept.push(photo);
        }
        return kept;
    };

    const handleSubmit = async () => {
        if (handoverType === 'now') {
            if (!handoverDateTime) {
                setError(t('pos.handover.rentalStartRequired'));
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const dateObj = new Date(handoverDateTime);
                const handoverTimestamp = dateObj.toISOString();
                const photosToSend = trimPhotosForPayload(handoverPhotos);

                const updatedBooking = await bookingService.handoverVehicle(currentBooking._id, {
                    fuelLevel: Number(fuelLevel) || 0,
                    mileage: Number(odometer) || 0,
                    condition: 'good',
                    photos: photosToSend,
                    notes: notes,
                    handedOverBy: 'Staff',
                    contractReference: contractReference.trim() || undefined,
                    actualHandoverTime: handoverTimestamp
                } as any);

                const expensePayloads = buildHandoverExpensePayloads(handoverTimestamp);
                const expenseResults = await Promise.allSettled(
                    expensePayloads.map((payload) => expenseService.createExpense(payload))
                );

                onHandoverComplete(updatedBooking, {
                    trackedExpenseCount: expenseResults.filter((result) => result.status === 'fulfilled').length,
                    failedExpenseCount: expenseResults.filter((result) => result.status === 'rejected').length,
                    photoCount: photosToSend.length,
                });
                onClose();
            } catch (err: any) {
                console.error(err);
                const status = err?.response?.status;
                if (!err?.response && handoverPhotos.length > 0) {
                    setError(t('pos.handover.networkRetryWithoutPhotos', {
                        defaultValue: 'Network error sending handover. Try again, or remove some photos if the problem continues.',
                    }));
                } else if (status === 413) {
                    setError(t('pos.handover.payloadTooLarge', {
                        defaultValue: 'Handover payload is too large. Please remove some photos and try again.',
                    }));
                } else {
                    setError(err?.response?.data?.message || t('pos.handover.processFailed'));
                }
            } finally {
                setLoading(false);
            }
        } else {
            // Schedule Later logic
            if (!scheduledHandoverDateTime) {
                setError(t('pos.handover.scheduledTimeRequired'));
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const scheduledTimestamp = new Date(scheduledHandoverDateTime).toISOString();
                const photosToSend = trimPhotosForPayload(handoverPhotos);

                const updatedBooking = await bookingService.handoverVehicle(currentBooking._id, {
                    scheduledHandoverTime: scheduledTimestamp,
                    contractReference: contractReference.trim() || undefined,
                    notes: notes || undefined,
                    handedOverBy: 'Staff',
                    photos: photosToSend,
                } as any);

                onHandoverComplete(updatedBooking, {
                    trackedExpenseCount: 0,
                    failedExpenseCount: 0,
                    photoCount: photosToSend.length,
                });
                onClose();
            } catch (err: any) {
                console.error(err);
                const status = err?.response?.status;
                if (!err?.response && handoverPhotos.length > 0) {
                    setError(t('pos.handover.networkRetryWithoutPhotos', {
                        defaultValue: 'Network error sending handover. Try again, or remove some photos if the problem continues.',
                    }));
                } else if (status === 413) {
                    setError(t('pos.handover.payloadTooLarge', {
                        defaultValue: 'Handover payload is too large. Please remove some photos and try again.',
                    }));
                } else {
                    setError(err?.response?.data?.message || t('pos.handover.scheduleFailed'));
                }
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <>
            <Dialog
                open={open}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        overflow: 'hidden'
                    }
                }}
                scroll="paper"
                BackdropProps={{
                    sx: { backgroundColor: 'rgba(29, 42, 76, 0.7)', backdropFilter: 'blur(4px)' }
                }}
                onClose={(_, reason) => {
                    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                        onClose();
                    }
                }}
            >
                <DialogContent sx={{ p: { xs: 2.5, md: 3 }, pb: 3, maxHeight: '85vh', overflowY: 'auto' }}>
                    {/* Header */}
                    <Box textAlign="center" mb={2.5}>
                        <Typography variant="h5" fontWeight="700" gutterBottom sx={{
                            background: 'linear-gradient(135deg, #273B7D 0%, #FF690F 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 0.5
                        }}>
                            {t('pos.handover.title', { defaultValue: 'Vehicle Handover' })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                            {t('bookings.bookingNumber')} #{currentBooking.bookingNumber}
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Handover Type Toggle */}
                    <Box mb={3}>
                        <ToggleButtonGroup
                            value={handoverType}
                            exclusive
                            onChange={(_, newValue) => newValue && setHandoverType(newValue)}
                            fullWidth
                            sx={{
                                '& .MuiToggleButton-root': {
                                    py: 1.5,
                                    borderRadius: '12px',
                                    border: '2px solid',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    transition: 'all 0.3s ease',
                                    '&:first-of-type': {
                                        marginRight: 1
                                    },
                                    '&.Mui-selected': {
                                        zIndex: 1
                                    }
                                },
                                '& .MuiToggleButton-root[value="now"]': {
                                    borderColor: '#FF690F',
                                    color: '#FF690F',
                                    '&.Mui-selected': {
                                        background: 'linear-gradient(135deg, rgba(255, 105, 15, 0.15) 0%, rgba(255, 105, 15, 0.05) 100%)',
                                        borderColor: '#FF690F',
                                        color: '#FF690F',
                                        fontWeight: 700,
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, rgba(255, 105, 15, 0.2) 0%, rgba(255, 105, 15, 0.08) 100%)'
                                        }
                                    },
                                    '&:hover': {
                                        background: 'rgba(255, 105, 15, 0.05)'
                                    }
                                },
                                '& .MuiToggleButton-root[value="later"]': {
                                    borderColor: '#499FDD',
                                    color: '#499FDD',
                                    '&.Mui-selected': {
                                        background: 'linear-gradient(135deg, rgba(73, 159, 221, 0.15) 0%, rgba(73, 159, 221, 0.05) 100%)',
                                        borderColor: '#499FDD',
                                        color: '#499FDD',
                                        fontWeight: 700,
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, rgba(73, 159, 221, 0.2) 0%, rgba(73, 159, 221, 0.08) 100%)'
                                        }
                                    },
                                    '&:hover': {
                                        background: 'rgba(73, 159, 221, 0.05)'
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="now">
                                <CarIcon sx={{ mr: 1, fontSize: 20 }} />
                                {t('pos.handoverNow')}
                            </ToggleButton>
                            <ToggleButton value="later">
                                <ScheduleIcon sx={{ mr: 1, fontSize: 20 }} />
                                {t('pos.scheduleLater')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {(
                        <Card
                            sx={{
                                mb: 2,
                                borderRadius: '16px',
                                border: '1px solid rgba(39, 59, 125, 0.12)',
                                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 249, 255, 0.96) 100%)',
                            }}
                        >
                            <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={1.5}>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={800} color="#273B7D">
                                            {t('pos.handover.photosTitle')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('pos.handover.photosDescription')}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        size="small"
                                        label={`${handoverPhotos.length}/4`}
                                        sx={{
                                            bgcolor: handoverPhotos.length ? 'rgba(94, 189, 129, 0.14)' : 'rgba(39, 59, 125, 0.08)',
                                            color: handoverPhotos.length ? '#2E7D32' : '#273B7D',
                                            fontWeight: 700,
                                        }}
                                    />
                                </Box>

                                <input
                                    ref={photoInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    hidden
                                    onChange={(e) => handlePhotoFiles(e.target.files)}
                                />

                                <Button
                                    variant="outlined"
                                    startIcon={<PhotoCameraIcon />}
                                    onClick={() => photoInputRef.current?.click()}
                                    sx={{
                                        borderRadius: '10px',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: '#273B7D',
                                        color: '#273B7D',
                                        mb: handoverPhotos.length ? 2 : 0,
                                    }}
                                >
                                    {t('pos.handover.addPhotos')}
                                </Button>

                                {handoverPhotos.length > 0 && (
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                            gap: 1.25,
                                        }}
                                    >
                                        {handoverPhotos.map((src, index) => (
                                            <Card
                                                key={`${src.slice(0, 24)}-${index}`}
                                                sx={{
                                                    position: 'relative',
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                    border: '1px solid rgba(39, 59, 125, 0.12)',
                                                }}
                                            >
                                                <CardMedia
                                                    component="img"
                                                    height="96"
                                                    image={src}
                                                    alt={t('pos.handover.photoAlt', { number: index + 1 })}
                                                    sx={{ objectFit: 'cover' }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeHandoverPhoto(index)}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 6,
                                                        right: 6,
                                                        bgcolor: 'rgba(255,255,255,0.88)',
                                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.98)' }
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Card>
                                        ))}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}


                    {/* Form Fields */}
                    {handoverType === 'now' ? (
                        <Grid container spacing={1.5}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label={t('pos.handover.rentalStartTimeRequired')}
                                    type="datetime-local"
                                    fullWidth
                                    value={handoverDateTime}
                                    onChange={(e) => setHandoverDateTime(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            '&:hover fieldset': { borderColor: '#273B7D' },
                                            '&.Mui-focused fieldset': { borderColor: '#273B7D', borderWidth: 2 }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label={t('pos.handover.contractReference', { defaultValue: 'Contract Number / ID' })}
                                    fullWidth
                                    value={contractReference}
                                    onChange={(e) => setContractReference(e.target.value)}
                                    placeholder={t('pos.handover.contractReferencePlaceholder', {
                                        defaultValue: 'e.g. CN-2026-0142 or CT-8819'
                                    })}
                                    helperText={t('pos.handover.contractReferenceHelp', {
                                        defaultValue: 'Track the client contract linked to this handover.'
                                    })}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            '&:hover fieldset': { borderColor: '#273B7D' },
                                            '&.Mui-focused fieldset': { borderColor: '#273B7D', borderWidth: 2 }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label={t('pos.handover.currentOdometer')}
                                    type="number"
                                    fullWidth
                                    value={odometer}
                                    onChange={(e) => setOdometer(e.target.value ? Number(e.target.value) : '')}
                                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">km</InputAdornment>,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <OdometerIcon sx={{ color: '#273B7D' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            '&:hover fieldset': { borderColor: '#273B7D' },
                                            '&.Mui-focused fieldset': { borderColor: '#273B7D', borderWidth: 2 }
                                        },
                                        '& input[type=number]': {
                                            MozAppearance: 'textfield',
                                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                                WebkitAppearance: 'none',
                                                margin: 0
                                            }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel sx={{ '&.Mui-focused': { color: '#273B7D' } }}>{t('pos.handover.fuelLevel')}</InputLabel>
                                    <Select
                                        value={fuelLevel}
                                        onChange={(e) => setFuelLevel(e.target.value as number)}
                                        label={t('pos.handover.fuelLevel')}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <FuelIcon sx={{ color: '#273B7D' }} />
                                            </InputAdornment>
                                        }
                                        sx={{
                                            borderRadius: '12px',
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#273B7D' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#273B7D', borderWidth: 2 }
                                        }}
                                    >
                                        {FUEL_LEVELS.map((level) => (
                                            <MenuItem key={level.value} value={level.value}>
                                                {t(`pos.handover.fuelLevels.${level.key}`, { defaultValue: level.defaultLabel })}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    label={t('pos.handover.notesOptional')}
                                    multiline
                                    rows={2}
                                    fullWidth
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder={t('pos.handover.notesPlaceholder')}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            '&:hover fieldset': { borderColor: '#273B7D' },
                                            '&.Mui-focused fieldset': { borderColor: '#273B7D', borderWidth: 2 }
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container spacing={1.5}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label={t('pos.scheduledHandover')}
                                    type="datetime-local"
                                    fullWidth
                                    value={scheduledHandoverDateTime}
                                    onChange={(e) => setScheduledHandoverDateTime(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    variant="outlined"
                                    required
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            '&:hover fieldset': { borderColor: '#273B7D' },
                                            '&.Mui-focused fieldset': { borderColor: '#273B7D', borderWidth: 2 }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label={t('pos.handover.contractReference', { defaultValue: 'Contract Number / ID' })}
                                    fullWidth
                                    value={contractReference}
                                    onChange={(e) => setContractReference(e.target.value)}
                                    placeholder={t('pos.handover.contractReferencePlaceholder', {
                                        defaultValue: 'e.g. CN-2026-0142 or CT-8819'
                                    })}
                                    helperText={t('pos.handover.contractReferenceHelp', {
                                        defaultValue: 'Track the client contract linked to this handover.'
                                    })}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            '&:hover fieldset': { borderColor: '#273B7D' },
                                            '&.Mui-focused fieldset': { borderColor: '#273B7D', borderWidth: 2 }
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1 }}>
                                    {t('pos.handover.scheduleHelper')}
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                    {t('pos.handover.schedulePendingWarning', {
                                        defaultValue: 'Scheduling does NOT hand the vehicle over now. The booking stays confirmed and pending — including any balance due — until the scheduled time is reached (or you complete it with "Now"). It will keep appearing in the handover list until then.',
                                    })}
                                </Alert>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    label={t('pos.handover.notesOptional')}
                                    multiline
                                    rows={2}
                                    fullWidth
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder={t('pos.handover.notesPlaceholder')}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px',
                                            '&:hover fieldset': { borderColor: '#273B7D' },
                                            '&.Mui-focused fieldset': { borderColor: '#273B7D', borderWidth: 2 }
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {/* Balance Due Box */}
                    <Box mt={2}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: '12px',
                                        background: balanceDue > 0
                                            ? 'linear-gradient(135deg, rgba(227, 71, 18, 0.08) 0%, rgba(227, 71, 18, 0.02) 100%)'
                                            : 'linear-gradient(135deg, rgba(94, 189, 129, 0.08) 0%, rgba(94, 189, 129, 0.02) 100%)',
                                        border: `2px solid ${balanceDue > 0 ? 'rgba(227, 71, 18, 0.3)' : 'rgba(94, 189, 129, 0.3)'}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                            {t('pos.handover.balanceDue')}
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight="800"
                                            sx={{
                                                color: balanceDue > 0 ? '#E34712' : '#5EBD81',
                                                fontSize: '1.3rem'
                                            }}
                                        >
                                            {currentBooking.pricing.currency} {balanceDue.toFixed(2)}
                                        </Typography>
                                    </Box>
                                    {balanceDue > 0 ? (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<PaymentIcon />}
                                            onClick={() => setPaymentDialogOpen(true)}
                                            sx={{
                                                background: '#5EBD81',
                                                color: 'white',
                                                borderRadius: '8px',
                                                fontWeight: 700,
                                                textTransform: 'none',
                                                boxShadow: '0 4px 12px rgba(94, 189, 129, 0.3)',
                                                '&:hover': {
                                                    background: '#4AA870',
                                                    boxShadow: '0 6px 16px rgba(94, 189, 129, 0.4)'
                                                }
                                            }}
                                        >
                                            {t('common.record', { defaultValue: 'Record' })}
                                        </Button>
                                    ) : (
                                        <CheckIcon sx={{ color: '#5EBD81', fontSize: 32 }} />
                                    )}
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Card
                                    sx={{
                                        borderRadius: '16px',
                                        border: '1px solid rgba(39, 59, 125, 0.12)',
                                        background: 'linear-gradient(180deg, rgba(248, 249, 255, 0.96) 0%, rgba(255, 255, 255, 0.98) 100%)',
                                    }}
                                >
                                    <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={1.5}>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={800} color="#273B7D">
                                                    {t('pos.handover.internalExpensesTitle', { defaultValue: 'Internal expense tracking' })}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t('pos.handover.internalExpensesDescription', {
                                                        defaultValue: 'Record fuel or other handover costs here. They will be saved to the expense ledger only and will not affect the customer invoice.',
                                                    })}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                                            {t('pos.handover.internalExpensesNote', {
                                                defaultValue: 'These amounts are tracked as agency expenses and do not change the customer balance due.',
                                            })}
                                        </Alert>

                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <TextField
                                                    label={t('pos.handover.fuelExpenseAmount', { defaultValue: 'Fuel expense amount' })}
                                                    type="number"
                                                    fullWidth
                                                    value={fuelExpenseAmount}
                                                    onChange={(e) => setFuelExpenseAmount(e.target.value ? Number(e.target.value) : '')}
                                                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                                    InputProps={{
                                                        endAdornment: <InputAdornment position="end">{currentBooking.pricing.currency || 'TND'}</InputAdornment>,
                                                    }}
                                                    helperText={t('pos.handover.fuelExpenseHelper', {
                                                        defaultValue: 'Optional. Use this for top-up fuel costs paid by the agency.',
                                                    })}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '12px',
                                                            '&:hover fieldset': { borderColor: '#273B7D' },
                                                            '&.Mui-focused fieldset': { borderColor: '#273B7D', borderWidth: 2 }
                                                        },
                                                        '& input[type=number]': {
                                                            MozAppearance: 'textfield',
                                                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                                                WebkitAppearance: 'none',
                                                                margin: 0
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <TextField
                                                    label={t('pos.handover.otherExpenseAmount', { defaultValue: 'Washing expense amount' })}
                                                    type="number"
                                                    fullWidth
                                                    value={otherExpenseAmount}
                                                    onChange={(e) => setOtherExpenseAmount(e.target.value ? Number(e.target.value) : '')}
                                                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                                    InputProps={{
                                                        endAdornment: <InputAdornment position="end">{currentBooking.pricing.currency || 'TND'}</InputAdornment>,
                                                    }}
                                                    helperText={t('pos.handover.otherExpenseHelper', {
                                                        defaultValue: 'Optional. Use this for washing or light cleaning at handover.',
                                                    })}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '12px',
                                                            '&:hover fieldset': { borderColor: '#273B7D' },
                                                            '&.Mui-focused fieldset': { borderColor: '#273B7D', borderWidth: 2 }
                                                        },
                                                        '& input[type=number]': {
                                                            MozAppearance: 'textfield',
                                                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                                                WebkitAppearance: 'none',
                                                                margin: 0
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <TextField
                                                    label={t('pos.handover.fuelExpenseDetails', { defaultValue: 'Fuel expense details' })}
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    value={fuelExpenseDetails}
                                                    onChange={(e) => setFuelExpenseDetails(e.target.value)}
                                                    placeholder={t('pos.handover.fuelExpensePlaceholder', {
                                                        defaultValue: 'e.g. Fuel top-up before handover, station name, receipt note...',
                                                    })}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '12px',
                                                            '&:hover fieldset': { borderColor: '#273B7D' },
                                                            '&.Mui-focused fieldset': { borderColor: '#273B7D', borderWidth: 2 }
                                                        }
                                                    }}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <TextField
                                                    label={t('pos.handover.otherExpenseDetails', { defaultValue: 'Washing expense details' })}
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    value={otherExpenseDetails}
                                                    onChange={(e) => setOtherExpenseDetails(e.target.value)}
                                                    placeholder={t('pos.handover.otherExpensePlaceholder', {
                                                        defaultValue: 'e.g. Car wash, exterior cleaning, receipt note...',
                                                    })}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '12px',
                                                            '&:hover fieldset': { borderColor: '#273B7D' },
                                                            '&.Mui-focused fieldset': { borderColor: '#273B7D', borderWidth: 2 }
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                        </Grid>
                    </Box>

                    {/* Action Buttons */}
                    <Box mt={3} display="flex" gap={2}>
                        <Button
                            onClick={onClose}
                            sx={{
                                flex: 1,
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 600,
                                color: '#499FDD',
                                border: '2px solid #499FDD',
                                py: 1.2,
                                '&:hover': {
                                    background: 'rgba(73, 159, 221, 0.08)',
                                    borderColor: '#3A8ACC'
                                }
                            }}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                            sx={{
                                flex: 2,
                                background: 'linear-gradient(135deg, #273B7D 0%, #499FDD 100%)',
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '1rem',
                                py: 1.2,
                                boxShadow: '0 8px 24px rgba(39, 59, 125, 0.3)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #1D2A4C 0%, #3A8ACC 100%)',
                                    boxShadow: '0 12px 32px rgba(39, 59, 125, 0.4)',
                                    transform: 'translateY(-2px)'
                                },
                                '&:disabled': {
                                    background: '#ccc'
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            {loading
                                ? t('pos.handover.processing')
                                : handoverType === 'now'
                                    ? t('pos.handover.confirmHandover')
                                    : t('pos.handover.scheduleHandoverButton', {
                                        defaultValue: 'Schedule handover (keeps booking pending)',
                                    })}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            <PaymentDialog
                open={paymentDialogOpen}
                onClose={() => setPaymentDialogOpen(false)}
                onSubmit={handleAddPayment}
                bookingNumber={currentBooking.bookingNumber}
                totalAmount={currentBooking.pricing.totalAmount}
                paidAmount={currentBooking.pricing.paidAmount}
                balanceAmount={balanceDue}
                paymentStatus={currentBooking.paymentStatus}
                showBalanceTimingOption={true}
            />
        </>
    );
};

export default HandoverWindow;
